import {
    StoreReference,
    useStore,
} from '@form-engine/core-application/hooks/use-store';
import { getStorageNamespace } from '@form-engine/core-util/local-storage';
import React, { createContext, useContext, useMemo } from 'react';
import { z } from 'zod';

export const FormEditorContextValueSchema = z
    .object({
        view: z.string().default('form'),
        leftDrawerTab: z.string().default('outline'),
        canvasTab: z.enum(['edit', 'preview', 'translate']).default('edit'),
        rightDrawerTab: z.string().default('settings'),
        previewSettings: z
            .object({
                locale: z.string().optional().default('default'),
                auth: z.enum(['basic', 'bearer']).default('basic'),
                basicAuthUsername: z.string().optional(),
                basicAuthPassword: z.string().optional(),
                bearerToken: z.string().optional(),
                customVariables: z
                    .array(z.object({ key: z.string(), value: z.string() }))
                    .default([]),
            })
            .default({}),
        selectedWorkflowLayerId: z.string().optional(),
        selectedDatasourceId: z.string().optional(),
        isDragging: z.boolean().default(false),
        sourceType: z.enum(['element', 'preset']).optional(),
        source: z.record(z.unknown()).optional(),
        selectedId: z.string().optional(),
        outlineCollapseList: z.array(z.string()).default([]),
        translationLocale: z.string().optional(),
        datasources: z.record(z.unknown()).optional(),
        workflowLayers: z.record(z.unknown()).optional(),
        constants: z.record(z.unknown()).optional(),
    })
    .and(z.record(z.unknown()));

export interface FormEditorContextValue
    extends z.infer<typeof FormEditorContextValueSchema> {}

export type FormEditorEditMode = FormEditorContextValue['canvasTab'];

export type FormEditorPreviewSettings =
    FormEditorContextValue['previewSettings'];

export const FormEditorContext =
    createContext<StoreReference<FormEditorContextValue> | null>(null);

const formEditorStorageNamespace = getStorageNamespace('febEditor');

export const formEditorStorage = {
    ...formEditorStorageNamespace,
    /**
     * drops expensive fields that should be omitted from local storage
     * @see https://ebike.atlassian.net/browse/CSPI-549
     */
    setItem(key: string, value?: FormEditorContextValue) {
        if (value) {
            value = {
                ...value,
                formHistory: [],
                formRedoHistory: [],
            };
        }
        return formEditorStorageNamespace.setItem(key, value);
    },
};

const FormEditorContextValueSchemaDefault = FormEditorContextValueSchema.parse(
    {}
);

function useSchemaResult<T extends z.ZodTypeAny = z.ZodTypeAny>(
    schema: T,
    data: unknown,
    fallback?: unknown
) {
    return useMemo(() => {
        let result = schema.safeParse(data);
        if (!result.success) {
            result = schema.safeParse(fallback ?? {});
            if (!result.success) {
                throw new Error('could not parse schema');
            }
            console.warn('got broken schema data using fallback');
        }
        return result.data as z.infer<T>;
    }, [schema, data, fallback]);
}

export interface FormEditorProviderProps {
    value: Partial<FormEditorContextValue>;
    children?:
        | React.ReactNode
        | ((store: StoreReference<FormEditorContextValue>) => React.ReactNode);
}

export function FormEditorProvider({
    value: initialState,
    children,
}: FormEditorProviderProps) {
    const store = useStore(
        useSchemaResult(
            FormEditorContextValueSchema,
            initialState,
            FormEditorContextValueSchemaDefault
        )
    );
    if (!store) return null;
    return (
        <FormEditorContext.Provider value={store}>
            {typeof children === 'function' ? children(store) : children}
        </FormEditorContext.Provider>
    );
}

export function useFormEditorContext() {
    const context = useContext(FormEditorContext);
    if (!context) {
        throw new Error('tried to use form editor context without provider');
    }
    return context;
}
